import { pinProduct, pinVariants, unPinProduct, unpinAllVariants } from '@/api/sale-api';
import { toast } from '@/hooks/use-toast';
import { TProductWithRelation } from '@/types/product-type';
import { PinnedVariantTab, PinnedVariantWithStock } from '@/types/sale-type';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { SALE_PINS_KEY } from '../lib/sale-cache-keys';
import { mergePinCacheByOrder, toPinnedStock } from '../lib/sale-stock-utils';

type UseSalePinsDataOptions = {
  products: TProductWithRelation[];
  patchProductPinState: (variantId: string, pinned: boolean) => void;
  refreshSaleProduct: () => void;
};

async function fetchPinVariants(): Promise<PinnedVariantWithStock[]> {
  const resp = await pinVariants();
  if (!resp.success) {
    throw new Error(resp.message || 'Cannot fetch pinned variants');
  }
  return resp.data;
}

export function useSalePinsData({
  products,
  patchProductPinState,
  refreshSaleProduct,
}: UseSalePinsDataOptions) {
  const [activePinTab, setActivePinTab] = useState('all');
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const pinnedIdsRef = useRef<string[]>([]);
  const activePinTabRef = useRef(activePinTab);
  const hasSeededIdsRef = useRef(false);
  const pinSyncRef = useRef<Promise<void>>(Promise.resolve());

  pinnedIdsRef.current = pinnedIds;
  activePinTabRef.current = activePinTab;

  const runPinSync = useCallback(<T,>(operation: () => Promise<T>): Promise<T> => {
    const next = pinSyncRef.current.then(operation, operation);
    pinSyncRef.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }, []);

  const { data: pinnedVariants = [], mutate, isLoading: isLoadingPins } = useSWR(
    SALE_PINS_KEY,
    fetchPinVariants,
    {
      revalidateOnFocus: false,
      // Pins are seeded from productForSale; avoid duplicate pinVariants on mount.
      revalidateOnMount: false,
      revalidateIfStale: false,
    },
  );

  useEffect(() => {
    if (pinnedVariants.length > 0 && !hasSeededIdsRef.current) {
      setPinnedIds(pinnedVariants.map((item) => item.id));
      hasSeededIdsRef.current = true;
    }
  }, [pinnedVariants]);

  const handleRefreshPins = useCallback(async () => {
    const fresh = await fetchPinVariants();
    await mutate(fresh, { revalidate: false });
    setPinnedIds(fresh.map((item) => item.id));
  }, [mutate]);

  const getPinVariant = useCallback(
    (variantId: string): PinnedVariantWithStock | null => {
      const existing = pinnedVariants.find((v) => v.id === variantId);
      if (existing) return existing;

      const product = products.find((p) => p.productVariants.some((v) => v.id === variantId));
      const variant = product?.productVariants.find((v) => v.id === variantId);
      if (!variant || !product) return null;
      return {
        id: variantId,
        product: product.product,
        variant,
        ...toPinnedStock(variant, product.product),
      };
    },
    [products, pinnedVariants],
  );

  const activeVariantExists =
    activePinTab === 'all' ||
    pinnedIds.includes(activePinTab) ||
    products.some((p) => p.productVariants.some((v) => v.id === activePinTab)) ||
    pinnedVariants.some((v) => v.id === activePinTab);

  useEffect(() => {
    if (!activeVariantExists) setActivePinTab('all');
  }, [activeVariantExists]);

  const pinnedVariantTabs = useMemo<PinnedVariantWithStock[]>(() => {
    if (pinnedIds.length === 0) return [];
    const variantMap = new Map(pinnedVariants.map((v) => [v.id, v]));
    return pinnedIds.map((id) => variantMap.get(id)).filter((v): v is PinnedVariantWithStock => !!v);
  }, [pinnedVariants, pinnedIds]);

  const pinnedIdSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const isPinned = useCallback((id: string) => pinnedIdSet.has(id), [pinnedIdSet]);

  const addPinnedId = useCallback(
    (id: string, productId: string, stubOverride?: PinnedVariantWithStock | null) => {
      const stub = stubOverride ?? getPinVariant(id);
      setPinnedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      if (stub) {
        void mutate(
          (current = []) => (current.some((p) => p.id === id) ? current : [...current, stub]),
          { revalidate: false },
        );
      }

      return runPinSync(async () => {
        const resp = await pinProduct(id, productId);
        if (resp.success) {
          void mutate(
            (current = []) =>
              mergePinCacheByOrder(pinnedIdsRef.current, resp.data, current),
            { revalidate: false },
          );
          patchProductPinState(id, true);
        } else {
          setPinnedIds((prev) => prev.filter((pinnedId) => pinnedId !== id));
          void mutate(
            (current = []) => current.filter((item) => item.id !== id),
            { revalidate: false },
          );
          toast({ title: 'Error', description: resp.message || 'Cannot pin variant', variant: 'destructive' });
        }
      });
    },
    [getPinVariant, mutate, patchProductPinState, runPinSync],
  );

  const removePinnedId = useCallback(
    (id: string, productId: string, options?: { keepActiveTab?: boolean }) => {
      const wasActive = activePinTabRef.current === id;
      const nextOrder = pinnedIdsRef.current.filter((pinnedId) => pinnedId !== id);

      if (wasActive && !options?.keepActiveTab) {
        setActivePinTab('all');
      }

      setPinnedIds(nextOrder);
      void mutate(
        (current = []) => mergePinCacheByOrder(nextOrder, current, current),
        { revalidate: false },
      );
      patchProductPinState(id, false);

      void runPinSync(async () => {
        const resp = await unPinProduct(id, productId);
        if (resp.success) {
          void mutate(
            (current = []) =>
              mergePinCacheByOrder(pinnedIdsRef.current, resp.data, current),
            { revalidate: false },
          );
        } else {
          const pinVariant = getPinVariant(id);
          setPinnedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
          if (pinVariant) {
            void mutate(
              (current = []) => (current.some((v) => v.id === id) ? current : [...current, pinVariant]),
              { revalidate: false },
            );
          }
          patchProductPinState(id, true);
          if (wasActive && !options?.keepActiveTab) {
            setActivePinTab(id);
          }
          toast({ title: 'Error', description: resp.message || 'Cannot unpin variant', variant: 'destructive' });
        }
      });
    },
    [getPinVariant, mutate, patchProductPinState, runPinSync],
  );

  const handleTogglePin = useCallback(
    (id: string, productId: string, pinned: boolean, stubOverride?: PinnedVariantWithStock) => {
      if (pinned) void addPinnedId(id, productId, stubOverride);
      else void removePinnedId(id, productId, { keepActiveTab: true });
    },
    [addPinnedId, removePinnedId],
  );

  const handleUnpinAll = useCallback(async () => {
    const idsToUnpin = [...pinnedIdsRef.current];
    setPinnedIds([]);
    setActivePinTab('all');
    await mutate([], { revalidate: false });
    idsToUnpin.forEach((id) => patchProductPinState(id, false));

    const resp = await unpinAllVariants();
    if (resp.success) {
      await refreshSaleProduct();
      await mutate([], { revalidate: false });
      toast({
        title: 'Success',
        description: 'All pinned variants have been unpinned successfully',
        variant: 'success',
      });
      return;
    }

    setPinnedIds(idsToUnpin);
    idsToUnpin.forEach((id) => patchProductPinState(id, true));
    void mutate();
    toast({
      title: 'Error',
      description: resp.message || 'Cannot unpin all variants',
      variant: 'destructive',
    });
  }, [mutate, patchProductPinState, refreshSaleProduct]);

  const handleReorder = useCallback((newOrder: PinnedVariantTab[]) => {
    setPinnedIds(newOrder.map((item) => item.id));
  }, []);

  const refreshPinVariants = useCallback(
    async ({ variantId, addNew }: { variantId: string; addNew: boolean }) => {
      if (addNew) {
        const pinVariant = getPinVariant(variantId);
        if (pinVariant) {
          await mutate(
            (current = []) => (current.some((p) => p.id === variantId) ? current : [...current, pinVariant]),
            { revalidate: false },
          );
        }
      } else {
        await mutate(
          (current = []) => current.filter((item) => item.id !== variantId),
          { revalidate: false },
        );
      }
      await mutate();
    },
    [getPinVariant, mutate],
  );

  return {
    pinnedVariants,
    activePinTab,
    setActivePinTab,
    pinnedVariantTabs,
    pinnedIdSet,
    isPinned,
    handleTogglePin,
    handleRemovePin: removePinnedId,
    handleUnpinAll,
    handleReorder,
    handleRefreshPins,
    refreshPinVariants,
    isLoadingPins,
  };
}
